// ISO 8601 weeks (Monday first, week 1 contains the first Thursday), computed in UTC so DST never shifts a day.
export type IsoWeek = { year: number; week: number };

const DAY_MS = 86_400_000;

function isoWeekOfUtc(utcMidnight: number): IsoWeek {
  const thursday = new Date(utcMidnight);
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const year = thursday.getUTCFullYear();
  const dayOfYear = (thursday.getTime() - Date.UTC(year, 0, 1)) / DAY_MS;
  return { year, week: Math.floor(dayOfYear / 7) + 1 };
}

/** The ISO week a (local) calendar date falls in. */
export function isoWeekOf(date: Date): IsoWeek {
  return isoWeekOfUtc(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/** Monday of the week, as UTC midnight. */
export function mondayOf({ year, week }: IsoWeek): Date {
  const jan4 = Date.UTC(year, 0, 4);
  const weekOneMonday = jan4 - ((new Date(jan4).getUTCDay() || 7) - 1) * DAY_MS;
  return new Date(weekOneMonday + (week - 1) * 7 * DAY_MS);
}

/** The ISO week of today (browser clock). */
export function currentWeek(): IsoWeek {
  return isoWeekOf(new Date());
}

export function shiftWeek(week: IsoWeek, delta: number): IsoWeek {
  return isoWeekOfUtc(mondayOf(week).getTime() + delta * 7 * DAY_MS);
}

export function sameWeek(a: IsoWeek, b: IsoWeek): boolean {
  return a.year === b.year && a.week === b.week;
}

/** "2 maart – 6 maart 2026" (Monday to Friday). */
export function weekRangeLabel(week: IsoWeek): string {
  const monday = mondayOf(week);
  const friday = new Date(monday.getTime() + 4 * DAY_MS);
  const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', ...options }).format(date);
  return `${format(monday, { day: 'numeric', month: 'long' })} – ${format(friday, { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

/** Route of one day of a week, e.g. `/week/2026/10/dag/2` (suffix `/digibord` for the board view). */
export function dayPath(week: IsoWeek, day: number, suffix = ''): string {
  return `/week/${week.year}/${week.week}/dag/${day}${suffix}`;
}

/** Previous/next school day; across the weekend it moves to the neighbouring week. */
export function neighbourDay(week: IsoWeek, day: number, delta: 1 | -1, suffix = ''): string {
  const next = day + delta;
  if (next >= 1 && next <= 5) return dayPath(week, next, suffix);
  return dayPath(shiftWeek(week, delta), delta === 1 ? 1 : 5, suffix);
}

/** Reads `:jaar` and `:week` route params; null when they are not a plausible ISO week. */
export function parseIsoWeek(params: { jaar?: string | undefined; week?: string | undefined }): IsoWeek | null {
  const year = Number(params.jaar);
  const week = Number(params.week);
  const valid = Number.isInteger(year) && year >= 2000 && year <= 2100 && Number.isInteger(week) && week >= 1 && week <= 53;
  return valid ? { year, week } : null;
}
